<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="onDialogInput">
    <v-card class="folder-run-compare">
      <v-card-title class="px-6">
        <h5 class="primary-color mb-0">Compare runs</h5>
        <v-spacer />
        <v-btn icon aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 py-2">
        <!-- Selectors -->
        <v-row dense class="folder-run-compare__pickers">
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedA"
              label="Run A (baseline)"
              :items="runItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="folder-run-compare__select-a"
              attach
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedB"
              label="Run B"
              :items="runItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="folder-run-compare__select-b"
              attach
            />
          </v-col>
        </v-row>

        <v-alert v-if="sameRun" dense text type="info" class="mt-4 mb-0">
          Pick two different runs to compare.
        </v-alert>

        <v-skeleton-loader v-else-if="loading" type="heading, table-row@5" class="mt-4" />

        <v-alert v-else-if="error" type="error" outlined dense class="mt-4 mb-0">
          <div class="d-flex align-center">
            <span>{{ error }}</span>
            <v-spacer />
            <v-btn small outlined color="error" @click="load">
              <v-icon small left>mdi-refresh</v-icon>
              Retry
            </v-btn>
          </div>
        </v-alert>

        <div v-else-if="!comparison" class="folder-run-compare__empty caption grey--text mt-6">
          Select a run on each side to see how they differ.
        </div>

        <template v-else>
          <!-- Comparability band: the first thing read, because it decides whether the
               numbers below mean anything. -->
          <v-alert
            :type="band.color"
            dense
            text
            class="mt-4 mb-4 folder-run-compare__band"
            :class="`folder-run-compare__band--${band.verdict}`"
          >
            <strong>{{ band.label }}.</strong>
            {{ band.text }}
            <span v-if="differingLabels" class="d-block caption mt-1">
              Differing: {{ differingLabels }}
            </span>
            <span v-if="comparabilityReason" class="d-block caption mt-1">
              {{ comparabilityReason }}
            </span>
          </v-alert>

          <!-- Provenance, side by side -->
          <v-row dense>
            <v-col v-for="side in sides" :key="side.key" cols="12" md="6">
              <v-card outlined class="folder-run-compare__side">
                <div class="folder-run-compare__side-head">
                  <span class="folder-run-compare__side-key">{{ side.key }}</span>
                  <strong class="ml-2">{{ side.title }}</strong>
                  <v-spacer />
                  <v-chip x-small label :color="side.status.color" dark>
                    {{ side.status.label }}
                  </v-chip>
                </div>
                <div class="caption grey--text text--darken-1">{{ side.caption }}</div>
                <div class="folder-run-compare__chips">
                  <FolderRunFreshnessChip :freshness="side.run && side.run.freshness" />
                  <v-chip x-small outlined label>{{ side.configLabel }}</v-chip>
                  <v-chip
                    v-for="engine in side.engines"
                    :key="`${side.key}-req-${engine}`"
                    x-small
                    outlined
                    label
                  >
                    {{ engine }}
                  </v-chip>
                </div>
                <div class="folder-run-compare__chips">
                  <v-chip
                    v-for="chip in side.engineVersions"
                    :key="`${side.key}-${chip.key}`"
                    x-small
                    outlined
                    label
                    :color="chip.color || undefined"
                    :title="chip.tooltip"
                  >
                    {{ chip.text }}
                  </v-chip>
                  <span v-if="!side.engineVersions.length" class="caption grey--text">
                    Engine versions not recorded
                  </span>
                </div>
                <FolderRunMetrics :metrics="side.run && side.run.metrics" class="mt-3" />
              </v-card>
            </v-col>
          </v-row>

          <!-- Delta -->
          <div class="folder-run-compare__section">Delta (B − A)</div>
          <v-simple-table dense class="folder-run-compare__table">
            <template #default>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th class="text-right">A</th>
                  <th class="text-right">B</th>
                  <th class="text-right">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in deltaRows" :key="row.key">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ row.a }}</td>
                  <td class="text-right">{{ row.b }}</td>
                  <td
                    class="text-right font-weight-bold folder-run-compare__delta"
                    :class="[row.deltaClass, { 'folder-run-compare__delta--muted': band.muted }]"
                    :title="band.text"
                  >
                    {{ row.delta }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <!-- Never silent: a non-attributable delta keeps its numbers but says so out loud. -->
          <div v-if="band.muted" class="folder-run-compare__warning caption error--text mt-2">
            <v-icon x-small left color="error">mdi-alert-circle-outline</v-icon>
            The delta is shown for reference only: {{ band.text.toLowerCase() }}
          </div>

          <!-- Per video -->
          <template v-if="perVideoRows.length">
            <div class="folder-run-compare__section">Per video ({{ perVideoRows.length }})</div>
            <v-simple-table dense class="folder-run-compare__table">
              <template #default>
                <thead>
                  <tr>
                    <th>Video</th>
                    <th>A</th>
                    <th>B</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in perVideoRows"
                    :key="row.key"
                    :class="{ 'folder-run-compare__row--changed': row.changed }"
                  >
                    <td>{{ row.name }}</td>
                    <td>
                      <v-chip x-small label :color="row.outcomeA.color" dark>
                        {{ row.outcomeA.label }}
                      </v-chip>
                    </td>
                    <td>
                      <v-chip x-small label :color="row.outcomeB.color" dark>
                        {{ row.outcomeB.label }}
                      </v-chip>
                    </td>
                    <td class="caption" :class="row.changed ? 'warning--text' : 'grey--text'">
                      {{ row.changeLabel }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <v-spacer />
        <v-btn text class="cancel-modal" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FolderRunFreshnessChip from '@/components/folderRuns/FolderRunFreshnessChip.vue';
import FolderRunMetrics from '@/components/folderRuns/FolderRunMetrics.vue';
import FolderRunsService from '@/services/FolderRunsService';
import {
  EMPTY,
  FOLDER_RUN_METRICS,
  comparabilityBand,
  configCaption,
  deltaColorClass,
  engineVersionChips,
  folderCaption,
  folderRunStatusPresentation,
  formatDateTime,
  formatDeltaPoints,
  formatPct,
  outcomePresentation,
  toNumber,
  variableLabel,
} from '@/utils/folderRuns';

/**
 * Compare two Folder Runs.
 *
 * The comparability band is the headline, not a footnote: with two or more variables changed a
 * delta cannot be credited to anything, so it is shown dimmed and with the warning next to it —
 * never hidden, because hiding it invites people to compute it by hand and trust it.
 */
export default {
  name: 'FolderRunCompareDialog',

  components: { FolderRunFreshnessChip, FolderRunMetrics },

  props: {
    value: { type: Boolean, default: false },
    /** FolderRunSummary list used to populate the two selectors. */
    runs: { type: Array, default: () => [] },
    /** Pre-selected ids (the detail page opens the dialog with itself on the right). */
    initialA: { type: [Number, String], default: null },
    initialB: { type: [Number, String], default: null },
  },

  data() {
    return {
      selectedA: null,
      selectedB: null,
      comparison: null,
      loading: false,
      error: '',
      // Opening the dialog assigns both selectors and calls load() once; the two watchers then
      // fire on nextTick for the same pair. This key collapses them into a single request.
      pendingKey: '',
    };
  },

  computed: {
    runItems() {
      return (Array.isArray(this.runs) ? this.runs : []).map((run) => ({
        value: Number(run.id),
        text: this.runOptionLabel(run),
      }));
    },
    sameRun() {
      return (
        this.selectedA != null &&
        this.selectedB != null &&
        Number(this.selectedA) === Number(this.selectedB)
      );
    },
    band() {
      return comparabilityBand(this.comparison?.comparability ?? this.comparison?.delta ?? null);
    },
    comparabilityReason() {
      const reason = this.comparison?.comparability?.reason;
      return typeof reason === 'string' && reason.trim() ? reason.trim() : '';
    },
    differingLabels() {
      return this.band.differing.map(variableLabel).join(', ');
    },
    sides() {
      return ['A', 'B'].map((key) => {
        const run = (key === 'A' ? this.comparison?.a : this.comparison?.b) || null;
        return {
          key,
          run,
          title: run?.name || folderCaption(run),
          caption: [
            run?.name ? folderCaption(run) : '',
            formatDateTime(run?.created_at),
            run?.build_sha ? `build ${String(run.build_sha).slice(0, 7)}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
          status: folderRunStatusPresentation(run?.status),
          configLabel: configCaption(run?.config),
          engines: Array.isArray(run?.engines) ? run.engines : [],
          engineVersions: engineVersionChips(run?.engine_versions, {
            outdatedEngines: run?.freshness?.outdated_engines,
          }),
        };
      });
    },
    deltaRows() {
      const a = this.comparison?.a?.metrics || null;
      const b = this.comparison?.b?.metrics || null;
      const delta = this.comparison?.delta || null;
      return FOLDER_RUN_METRICS.map((metric) => {
        const av = a ? toNumber(a[metric.key]) : null;
        const bv = b ? toNumber(b[metric.key]) : null;
        // Prefer the backend's own delta; fall back to B − A when it is absent.
        const explicit = delta ? toNumber(delta[metric.key]) : null;
        const value = explicit !== null ? explicit : av !== null && bv !== null ? bv - av : null;
        return {
          key: metric.key,
          label: metric.label,
          a: formatPct(av),
          b: formatPct(bv),
          delta: formatDeltaPoints(value),
          deltaClass: deltaColorClass(metric.key, value, this.band.verdict),
        };
      });
    },
    /**
     * `per_video` is documented only as a list in the contract, so every field is read
     * defensively and the whole section disappears when the shape is not what we expect.
     */
    perVideoRows() {
      const list = Array.isArray(this.comparison?.per_video) ? this.comparison.per_video : [];
      return list
        .map((entry, index) => {
          if (!entry || typeof entry !== 'object') return null;
          const left = entry.a && typeof entry.a === 'object' ? entry.a : entry;
          const right = entry.b && typeof entry.b === 'object' ? entry.b : null;
          const outcomeA = outcomePresentation(left.outcome ?? entry.outcome_a);
          const outcomeB = outcomePresentation(
            right ? right.outcome : (entry.outcome_b ?? entry.outcome)
          );
          const changed =
            typeof entry.changed === 'boolean' ? entry.changed : outcomeA.label !== outcomeB.label;
          return {
            key: `${entry.source_id ?? entry.id ?? index}`,
            name: entry.video_name || left.video_name || `Video ${entry.source_id ?? index + 1}`,
            outcomeA,
            outcomeB,
            changed,
            changeLabel: changed ? `${outcomeA.label} → ${outcomeB.label}` : 'unchanged',
          };
        })
        .filter((row) => row && (row.changed || !row.outcomeA.empty || !row.outcomeB.empty));
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
    selectedA() {
      this.load();
    },
    selectedB() {
      this.load();
    },
  },

  created() {
    if (this.value) this.reset();
  },

  methods: {
    runOptionLabel(run) {
      if (!run) return '';
      const parts = [run.name || folderCaption(run)];
      if (run.name) parts.push(folderCaption(run));
      parts.push(formatDateTime(run.created_at));
      return parts.filter((part) => part && part !== EMPTY).join(' · ');
    },

    reset() {
      this.error = '';
      this.comparison = null;
      const fallback = this.runItems.map((item) => item.value);
      this.selectedA =
        this.initialA != null ? Number(this.initialA) : (fallback[1] ?? fallback[0] ?? null);
      this.selectedB = this.initialB != null ? Number(this.initialB) : (fallback[0] ?? null);
      this.load();
    },

    async load() {
      if (this.selectedA == null || this.selectedB == null || this.sameRun) {
        this.comparison = null;
        this.pendingKey = '';
        return;
      }
      const key = `${this.selectedA}:${this.selectedB}`;
      if (this.loading && this.pendingKey === key) return;
      this.pendingKey = key;
      this.loading = true;
      this.error = '';
      try {
        const response = await FolderRunsService.compare(this.selectedA, this.selectedB);
        this.comparison = response?.data?.data ?? response?.data ?? null;
      } catch (error) {
        this.comparison = null;
        this.error = 'Could not compare these two runs.';
      } finally {
        this.loading = false;
      }
    },

    onDialogInput(open) {
      if (!open) this.close();
    },

    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.folder-run-compare__side {
  padding: 12px;
  border-radius: 8px !important;
  height: 100%;
}
.folder-run-compare__side-head {
  display: flex;
  align-items: center;
}
.folder-run-compare__side-key {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #9e9e9e;
}
.folder-run-compare__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.folder-run-compare__section {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin: 20px 0 6px;
}
.folder-run-compare__delta--muted {
  opacity: 0.5;
}
.folder-run-compare__warning {
  display: flex;
  align-items: center;
}
.folder-run-compare__row--changed {
  background-color: rgba(255, 152, 0, 0.06);
}
.folder-run-compare__empty {
  display: block;
  text-align: center;
  padding: 32px 0;
}
</style>
